import { arSA as ar, enGB as en } from 'date-fns/locale'
import { differenceInDays, format, parse } from 'date-fns'
import { DEFAULT_DATE, DEFAULT_DATE_AR } from '@/shared/constants/dates.constants'
import type { ApiErrorResponse } from '@/shared/types/response.types'
import type { ComposerTranslation } from 'vue-i18n'
import { nanoid } from 'nanoid'
import Visitor from '@/shared/models/Visitor'
import { VisitorType } from '@/shared/types/store.types'
import { AxiosError, isAxiosError } from 'axios'

export { capitalizeText, capitalizeFirstLetter } from './textUtils'

const numberFormat = new Intl.NumberFormat('ru-RU')

export const formatNumber = (value: number) => (value ? numberFormat.format(value) : '')

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

export function formatDate(value: Date, locale: LocaleType, mask?: string) {
  let formatString = locale === 'ar' ? 'd MMMM yyyy' : 'dd/MM/yyyy'
  if (mask) {
    formatString = mask
  }
  const formatLocale = locale === 'ar' ? ar : en
  const marker = locale === 'ar' ? '\u200f' : ''
  return value ? format(value, `${marker}${formatString}`, { locale: formatLocale }) : ''
}

export function setFormatDate(value: Date, locale: LocaleType) {
  return formatDate(value, locale, setFormat(locale))
}

export const daysBetween = (first: Date, second: Date) => differenceInDays(first, second)

export function parseDate(dateString: string, formatString: string): Date | null {
  const maybeDate = parse(dateString, formatString, new Date())
  return isDate(maybeDate) ? maybeDate : null
}

export const isDate = (maybeDate: unknown): boolean =>
  maybeDate instanceof Date && !isNaN(maybeDate.getDate())

const setFormat = (locale: LocaleType) => (locale === 'ar' ? DEFAULT_DATE_AR : DEFAULT_DATE)

export const dayNames = (t: ComposerTranslation): string[] =>
  ['day_1', 'day_2', 'day_3', 'day_4', 'day_5', 'day_6', 'day_7'].map(t)

export const persianDigits = [
  '\u0660',
  '\u0661',
  '\u0662',
  '\u0663',
  '\u0664',
  '\u0665',
  '\u0666',
  '\u0667',
  '\u0668',
  '\u0669',
]

export const persianToArabian = (str: string) =>
  str
    .split('')
    .map((number) => (isNaN(+number) ? persianDigits.indexOf(number) : number))
    .join()

export const is504 = (err: ApiErrorResponse) => err.response?.status === 504

export const newVisitor = (dto: VisitorType) => new Visitor(dto, nanoid())

export const isErrorWithRequiredEmptyParams = (
  error: unknown
): error is AxiosError<{ requiredEmptyParams?: any }> => {
  if (!isAxiosError(error)) return false

  const { data } = error.response || {}

  return !!(data && typeof data === 'object' && 'requiredEmptyParams' in data)
}

export const checkAgeForInboundAddon = (birthDate: Date | string) => {
  const today = new Date()
  const age = today.getFullYear() - new Date(birthDate).getFullYear()
  return age <= 65
}

export const getDateWithoutTime = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())
